/*
 * design-formula.js — wrap cut geometry, ported 1:1 from the fulfillment app
 * (GolfClubManager/processorders.py::calculate_cylinder_dimensions).
 *
 *   straight    = (rawLength + shaftOffset) - gripLen - 2.75   // wrap length, inches
 *   bottomWidth = PI * 0.365 * 1.1   (tip end, narrower)
 *   topWidth    = PI * 0.6   * 1.1   (grip end, wider)
 *   diagonal    = sqrt(straight^2 + topWidth^2)
 *
 * The trapezoid is SYMMETRIC (isosceles): the narrow tip edge is centered under the wide grip
 * edge, so BOTH side edges taper inward equally. (Butt/tip widths are unchanged — only the taper
 * is centered instead of falling entirely on the right edge.)
 * Rendered at 100 px/inch in production (display may scale down).
 *
 * raw_length (the club's length) lives in the "Irons Raw Data" Google Sheet, keyed by
 * Brand/Collection/Model/Year + per-club column. Export it to assets/irons-dimensions.json
 * (see create_products_file.py). Until a brand/year is present there, STANDARD_IRON_LENGTHS
 * is used as a fallback so the studio still renders.
 */
(function () {
  'use strict';

  var PI = 3.14159;

  var API = {
    PX_PER_INCH: 100,
    TOP_WIDTH: PI * 0.6 * 1.1,      // ~2.0735"
    BOTTOM_WIDTH: PI * 0.365 * 1.1, // ~1.2613"
    GRIP_DEFAULT: 10.0,
    TIP_TRIM: 2.75,

    // Fallback men's standard iron lengths (inches). Replaced per-club by sheet data when available.
    STANDARD_IRON_LENGTHS: {
      '3i': 39.0, '4i': 38.5, '5i': 38.0, '6i': 37.5, '7i': 37.0,
      '8i': 36.5, '9i': 36.0, 'PW': 35.5, 'AW/GW': 35.25, 'AW': 35.25, 'GW': 35.25
    },

    // Universal wraps are TOP-anchored (design anchored at the grip end; customer trims the tip
    // to fit). The wrap is cut to the longest club (the upper envelope), so any shorter club only
    // ever overlaps — never gaps. trimFraction = bottom portion that may be trimmed/overlapped on
    // shorter clubs (so it's the "at-risk" zone; the top is the safe, prime real estate).
    UNIVERSAL: {
      trimFraction: 0.25,
      adult: { straight: 27.253 },
      child: { straight: 23 }
    },

    // Driver wraps: universal only (no per-club custom), TOP-anchored exactly like the design iron
    // wrap. Widths vary by age (adult butt is fatter). Adult: butt π×0.62×1.1≈2.14", tip
    // π×0.335×1.1≈1.16", length 34". Kids: butt π×0.58×1.1≈2.00", tip 1.16", length 30".
    // (Sizes locked from the Universal Driver Wrap research.)
    DRIVER: {
      trimFraction: 0.25,
      adult: { straight: 34.0, topWidth: PI * 0.62 * 1.1, botWidth: PI * 0.335 * 1.1 },
      child: { straight: 30.0, topWidth: PI * 0.58 * 1.1, botWidth: PI * 0.335 * 1.1 }
    },

    // Active wrap widths (inches) for a club type + age. Iron widths are age-independent; driver
    // widths vary by age. clubType: 'iron' (default) | 'driver'.
    widthsFor: function (clubType, ageGroup) {
      if (clubType === 'driver') {
        var d = API.DRIVER[(ageGroup || 'adult').toLowerCase()] || API.DRIVER.adult;
        return { topWidth: d.topWidth, botWidth: d.botWidth };
      }
      return { topWidth: API.TOP_WIDTH, botWidth: API.BOTTOM_WIDTH };
    },

    // Universal wrap length (straight, inches) for a club type + age.
    universalStraight: function (clubType, ageGroup) {
      var age = (ageGroup || 'adult').toLowerCase();
      if (clubType === 'driver') { var d = API.DRIVER[age] || API.DRIVER.adult; return d.straight; }
      var u = API.UNIVERSAL[age] || API.UNIVERSAL.adult; return u.straight;
    },

    parseOffset: function (shaftLength) {
      if (shaftLength == null || shaftLength === '' || String(shaftLength).toLowerCase() === 'standard') return 0;
      var v = parseFloat(shaftLength);
      return isNaN(v) ? 0 : v;
    },

    parseGrip: function (gripLength) {
      var v = parseFloat(gripLength);
      return isNaN(v) ? API.GRIP_DEFAULT : v;
    },

    // The core formula. Returns the wrap length ("straight") in inches.
    computeStraight: function (opts) {
      opts = opts || {};
      var raw = Number(opts.rawLength) || 0;
      var straight = (raw + API.parseOffset(opts.shaftLength)) - API.parseGrip(opts.gripLength) - API.TIP_TRIM;
      return Math.max(0, straight);
    },

    // Full dimension set for a given raw length + adjustments.
    cylinderDimensions: function (opts) {
      var straight = API.computeStraight(opts);
      return {
        straight: round(straight, 2),
        bottomWidth: round(API.BOTTOM_WIDTH, 3),
        topWidth: round(API.TOP_WIDTH, 3),
        diagonal: round(Math.sqrt(straight * straight + API.TOP_WIDTH * API.TOP_WIDTH), 2)
      };
    },

    /*
     * Look up a club's raw length from the exported sheet data, falling back to standard.
     * ironsData shape: { brands: { <brandKey>: { <collKey>: { <modelKey>: { <year>: { "7i": 37.0, ... } } } } } }
     * Returns { rawLength:Number, source:'sheet'|'standard' }.
     */
    getRawLength: function (ironsData, sel, club) {
      try {
        var b = ironsData && ironsData.brands && ironsData.brands[sel.brandKey];
        var c = b && b[sel.collectionKey];
        var m = c && c[sel.modelKey];
        var y = m && m[sel.year];
        var v = y && (y[club] != null ? y[club] : y[club.replace('/GW', '').replace('/AW', '')]);
        if (v != null && !isNaN(parseFloat(v))) return { rawLength: parseFloat(v), source: 'sheet' };
      } catch (e) { /* fall through */ }
      var std = API.STANDARD_IRON_LENGTHS[club];
      return { rawLength: std != null ? std : 37.0, source: 'standard' };
    },

    // straight (inches) for a specific club, using sheet data when present.
    clubStraight: function (ironsData, sel, club, adjustments) {
      var raw = API.getRawLength(ironsData, sel, club).rawLength;
      return API.computeStraight({
        rawLength: raw,
        shaftLength: adjustments && adjustments.shaftLength,
        gripLength: adjustments && adjustments.gripLength
      });
    },

    /*
     * Trapezoid corner points for a given straight (inches) at a given px/inch scale.
     * Origin (0,0) = top-left of the bounding box. Top edge = grip end (wider), bottom = tip (narrower).
     * SYMMETRIC (isosceles): the tip edge is centered under the grip edge, so BOTH side edges taper
     * inward equally by `inset` = (top-bot)/2. The bounding box is topWidthPx wide (the max width).
     * Returns {points:[TL,TR,BR,BL], widthPx, heightPx, topWidthPx, botWidthPx}.
     * NOTE: with the centered taper, points[2].x/points[3].x are NOT the tip width — read botWidthPx.
     */
    trapezoidPoints: function (straightInches, pxPerInch, topWidthIn, botWidthIn) {
      var s = pxPerInch * straightInches;
      var top = pxPerInch * (topWidthIn != null ? topWidthIn : API.TOP_WIDTH);
      var bot = pxPerInch * (botWidthIn != null ? botWidthIn : API.BOTTOM_WIDTH);
      var inset = (top - bot) / 2; // each side angles in by half the width difference
      return {
        widthPx: Math.max(top, bot),
        heightPx: s,
        topWidthPx: top,
        botWidthPx: bot,
        points: [
          { x: 0, y: 0 },           // top-left (grip)
          { x: top, y: 0 },         // top-right (grip)
          { x: top - inset, y: s }, // bottom-right (tip) — centered
          { x: inset, y: s }        // bottom-left (tip) — centered
        ]
      };
    }
  };

  function round(n, d) { var f = Math.pow(10, d); return Math.round(n * f) / f; }

  window.ClubWrapDimensions = API;
})();
